import { cn } from '../../lib';
import { PhoneIcon } from 'lucide-react';
import { selectIsMinimalistic, useAppSelector } from '@/app/store';
import { useTranslation } from 'react-i18next';
import ru from 'react-phone-input-2/lang/ru.json';
import React, { useEffect, useRef, useState } from 'react';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import PhoneInput, { CountryData } from 'react-phone-input-2';

import 'react-phone-input-2/lib/style.css';
import styles from './PhoneInputComponent.module.scss';

// TypeScript interface for the component props
export interface PhoneInputComponentProps {
  error?: boolean;
  value?: string;
  onChange: (value: string, countryData: CountryData) => void;
  disabled?: boolean;
  className?: string;
  preferredCountries?: string[];
  onlyCountries?: string[];
  excludeCountries?: string[];
  enableSearch?: boolean;
  disableDropdown?: boolean;
  countryCodeEditable?: boolean;
}

// Main PhoneInput component
export const PhoneInputComponent: React.FC<PhoneInputComponentProps> = ({
  value = '',
  error = false,
  onChange,
  disabled = false,
  className = '',
  preferredCountries = ['ru', 'kz', 'by'],
  onlyCountries,
  excludeCountries,
  enableSearch = true,
  disableDropdown = false,
  countryCodeEditable = false,
}) => {
  const { t, i18n } = useTranslation();
  const isRU = i18n.language === 'ru';
  const isRTL = i18n.language === 'ar';
  const { countryCode } = useAppSelector((state) => state.application);
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  const [phoneValue, setPhoneValue] = useState<string>(value);
  const [country, setCountry] = useState<string>(countryCode);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>();

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const updateWidth = () => setTriggerWidth(wrapper.offsetWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  const handlePhoneChange = (value: string, countryData: CountryData) => {
    setPhoneValue(value);
    onChange(value, countryData);
  };

  useEffect(() => {
    if (value === '') return;

    try {
      const phoneNumber = parsePhoneNumberWithError(`+${value}`);
      if (phoneNumber.country) {
        setCountry(phoneNumber.country.toLowerCase());
        const timer = setTimeout(() => setPhoneValue(phoneNumber.number), 10);
        return () => clearTimeout(timer);
      }
    } catch {
      // Обработка невалидных номеров
    }
  }, [value]);

  return (
    <div
      ref={wrapperRef}
      style={triggerWidth ? ({ '--rpi-trigger-width': `${triggerWidth}px` } as React.CSSProperties) : undefined}
      className={cn(
        styles.phoneWrapper,
        error && styles['phoneWrapper--error'],
        isRTL && styles['phoneWrapper--rtl'],
        isMinimalistic && styles['phoneWrapper--minimalistic'],
        className,
      )}
    >
      {phoneValue === '' && country === '' && (
        <PhoneIcon size={16} className={cn(styles.phoneIcon, isRTL && styles['phoneIcon--rtl'])} />
      )}

      <PhoneInput
        country={country}
        disableSearchIcon
        disableCountryGuess
        preserveOrder={['preferredCountries']}
        localization={isRU ? ru : undefined}
        value={phoneValue}
        onChange={handlePhoneChange}
        placeholder={t('phone.selectPhonePrefix')}
        disabled={disabled}
        autoFormat={true}
        enableSearch={enableSearch}
        disableDropdown={disableDropdown}
        countryCodeEditable={countryCodeEditable}
        preferredCountries={preferredCountries}
        onlyCountries={onlyCountries}
        excludeCountries={excludeCountries}
        searchPlaceholder={t('phone.find')}
        searchNotFound={t('phone.noResults')}
        searchClass={cn(styles.reactPhoneInputSearch)}
        dropdownClass={cn(styles.reactPhoneInputDropdown)}
        containerClass={cn(styles.reactPhoneInputContainer)}
        buttonClass={cn(styles.reactPhoneInputButton, isRTL ? styles.rtlPhoneButton : styles.ltrPhoneButton)}
        inputStyle={{ textAlign: isRTL ? 'right' : 'left' }}
      />
    </div>
  );
};
